import { useEffect } from 'react';
import type { TrialFeature } from '@discover-frontend/components';

import {
  HX_TRIAL_PAYWALL_WINDOW_KEY,
  HxTrialPaywallWindowState,
  TRIAL_PAYWALL_WINDOW_EVENTS,
} from '../../../events';

type UseTrialPaywallWindowEventsParams = {
  isTrial: boolean;
  isLockedOut: boolean;
  openUnlockModal: (feature?: TrialFeature, category?: string) => void;
  markSessionQuotaExhausted: () => void;
};

const publishTrialPaywallContext = (state: HxTrialPaywallWindowState) => {
  (window as unknown as Record<string, unknown>)[HX_TRIAL_PAYWALL_WINDOW_KEY] =
    state;
  window.dispatchEvent(
    new CustomEvent(TRIAL_PAYWALL_WINDOW_EVENTS.TRIAL_PAYWALL_CONTEXT_CHANGED, {
      detail: state,
    })
  );
};

export const useTrialPaywallWindowEvents = ({
  isTrial,
  isLockedOut,
  openUnlockModal,
  markSessionQuotaExhausted,
}: UseTrialPaywallWindowEventsParams) => {
  useEffect(
    () => publishTrialPaywallContext({ isTrial, isLockedOut }),
    [isTrial, isLockedOut]
  );

  useEffect(() => {
    const handleOpenUnlockModal = (event: Event) => {
      if (isLockedOut) return;

      const detail = (
        event as CustomEvent<{ feature?: TrialFeature; category?: string }>
      ).detail;

      openUnlockModal(detail?.feature, detail?.category);
    };

    const handleTrialSessionExhausted = () => {
      if (isTrial) markSessionQuotaExhausted();
    };

    window.addEventListener(
      TRIAL_PAYWALL_WINDOW_EVENTS.OPEN_TRIAL_UNLOCK_MODAL,
      handleOpenUnlockModal
    );
    window.addEventListener(
      TRIAL_PAYWALL_WINDOW_EVENTS.TRIAL_SESSION_EXHAUSTED,
      handleTrialSessionExhausted
    );

    return () => {
      window.removeEventListener(
        TRIAL_PAYWALL_WINDOW_EVENTS.OPEN_TRIAL_UNLOCK_MODAL,
        handleOpenUnlockModal
      );
      window.removeEventListener(
        TRIAL_PAYWALL_WINDOW_EVENTS.TRIAL_SESSION_EXHAUSTED,
        handleTrialSessionExhausted
      );
    };
  }, [isTrial, isLockedOut, markSessionQuotaExhausted, openUnlockModal]);
};
